import React from 'react';
import { Check } from 'lucide-react';
import { buildRequesterTimeline, type RequesterTimelineTicket } from './requesterModel';
import { formatShortDate } from '../constants';

interface StatusTimelineProps {
  ticket: RequesterTimelineTicket;
}

/**
 * Stato della pratica in cima al dettaglio: cerchi pieni (tappe raggiunte) e
 * vuoti (ancora da percorrere) uniti da una linea. Verticale sul telefono,
 * orizzontale da `sm` in su: e UNA sola lista, impaginata in due modi, cosi
 * chi usa un lettore di schermo non trova due copie delle stesse tappe.
 *
 * Lo stato non e affidato al solo colore: c'e la spunta sulle tappe fatte,
 * l'anello su quella corrente e una parola nel testo per i lettori di schermo.
 */
const StatusTimeline: React.FC<StatusTimelineProps> = ({ ticket }) => {
  const steps = buildRequesterTimeline(ticket);
  if (steps.length === 0) return null;

  return (
    <ol
      aria-label="Stato della richiesta"
      className="flex flex-col sm:flex-row sm:items-start"
    >
      {steps.map((step, i) => {
        const isLast = i === steps.length - 1;
        const done = step.state === 'done';
        const current = step.state === 'current';
        const attesa = step.tone === 'attesa';

        const circle = current
          ? attesa
            ? 'bg-amber-500 border-amber-500 text-white ring-4 ring-amber-100 dark:ring-amber-900/40'
            : 'bg-indigo-600 border-indigo-600 text-white ring-4 ring-indigo-100 dark:ring-indigo-900/40'
          : done
            ? 'bg-emerald-500 border-emerald-500 text-white'
            : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-transparent';

        const label = current
          ? attesa
            ? 'text-amber-700 dark:text-amber-300 font-semibold'
            : 'text-indigo-700 dark:text-indigo-300 font-semibold'
          : done
            ? 'text-gray-700 dark:text-gray-200 font-medium'
            : 'text-gray-400 dark:text-gray-500';

        const line = done || current ? 'bg-indigo-200 dark:bg-indigo-800' : 'bg-gray-200 dark:bg-gray-700';

        return (
          <li
            key={step.key}
            aria-current={current ? 'step' : undefined}
            className="relative flex gap-2.5 pb-4 last:pb-0 sm:flex-col sm:flex-1 sm:gap-1.5 sm:pb-0 sm:min-w-0"
          >
            {/* Collegamento fra le tappe: decorativo, l'ordine lo da la lista. */}
            {!isLast && (
              <span
                aria-hidden="true"
                className={`absolute left-[9px] top-5 bottom-0 w-px sm:left-1/2 sm:right-0 sm:top-[9px] sm:bottom-auto sm:w-auto sm:h-px ${line}`}
              />
            )}
            <span
              aria-hidden="true"
              className={`relative z-[1] mt-px w-5 h-5 shrink-0 rounded-full border-2 flex items-center justify-center sm:mt-0 ${circle}`}
            >
              {done && <Check className="w-3 h-3" />}
              {current && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
            </span>
            <div className="min-w-0 sm:pr-3">
              <p className={`text-xs leading-snug ${label}`}>
                {step.label}
                <span className="sr-only">
                  {current ? ' — tappa in corso' : done ? ' — completata' : ' — da fare'}
                </span>
              </p>
              {step.at && (
                <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5 tabular-nums">
                  {formatShortDate(step.at)}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
};

export default StatusTimeline;
